import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useMBList } from '../hooks/useMBList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MBConfig = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const MB_KEYS = useMBList();
  const [labels, setLabels] = useState(() => readInstantCache('mb_config', 'all')?.labels || {});
  const [defaults, setDefaults] = useState(() => readInstantCache('mb_config', 'all')?.defaults || {});
  const [handlers, setHandlers] = useState(() => readInstantCache('mb_config', 'all')?.handlers || []);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(() => !readInstantCache('mb_config', 'all'));

  const load = useCallback(async () => {
    if (!readInstantCache('mb_config', 'all')) setLoading(true);
    try {
      const [cfg, handlersRes] = await Promise.all([
        axios.get(`${API}/mb-config`),
        axios.get(`${API}/users/handlers`),
      ]);
      setLabels(cfg.data.labels || {});
      setDefaults(cfg.data.default_handlers || {});
      setHandlers(handlersRes.data || []);
      writeInstantCache('mb_config', 'all', { labels: cfg.data.labels || {}, defaults: cfg.data.default_handlers || {}, handlers: handlersRes.data || [] });
    } catch (e) {
      toast.error('Failed to load MB config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/mb-config`, {
        actor_id: user?.id,
        labels,
        default_handlers: defaults,
      });
      // Cache labels in localStorage so other pages can read them without extra API call
      try {
        localStorage.setItem('mb_labels', JSON.stringify(labels));
        window.dispatchEvent(new Event('mb-labels-updated'));
      } catch {}
      toast.success('MB config saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col overflow-hidden" data-testid="mb-config-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-4 py-3 flex items-center gap-3 shadow flex-shrink-0">
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/admin-settings'); }} className="p-1.5 rounded-full hover:bg-white/20" data-testid="back-btn">
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">MB Setup Configuration</h1>
          <p className="text-xs opacity-90">Rename setups & auto-route clients to default handler</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto max-w-2xl mx-auto p-4 space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
          💡 यहाँ आप हर MB को custom नाम (जैसे "USDT", "UPI Fast") दे सकते हैं और हर MB का default handler set कर सकते हैं। जब कोई client उस MB में assign होगा, उसकी सारी active chats automatically उस handler को assign हो जाएँगी।
        </div>

        {MB_KEYS.map((mb) => (
          <div key={mb} className="bg-white rounded-lg shadow-sm border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{mb}</span>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600">Custom Label:</label>
              <input
                type="text"
                value={labels[mb] || ''}
                onChange={(e) => setLabels((p) => ({ ...p, [mb]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-teal-500 outline-none"
                placeholder={mb}
                data-testid={`label-${mb}`}
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600">Default Handler (auto-route clients):</label>
              <select
                value={defaults[mb] || ''}
                onChange={(e) => setDefaults((p) => ({ ...p, [mb]: e.target.value || null }))}
                className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                data-testid={`default-${mb}`}
              >
                <option value="">— No auto-route —</option>
                {handlers.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.username} ({(h.role || '').replace('_', ' ')})
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}

        <button
          onClick={save}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-600 text-white font-bold hover:bg-teal-700 disabled:opacity-50"
          data-testid="save-mb-config"
        >
          <FloppyDisk size={20} /> {saving ? 'Saving...' : 'Save MB Configuration'}
        </button>
      </div>
    </div>
  );
};

export default MBConfig;
